import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Play, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { mediaAPI, getErrorMessage } from '../../services/api';

interface VideoTrimmerProps {
  mediaId: string;
  videoUrl: string;
  duration: number;
  onTrimComplete: (newMedia: unknown) => void;
  onCancel: () => void;
}

/**
 * Format seconds to MM:SS display format
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function VideoTrimmer({
  mediaId,
  videoUrl,
  duration,
  onTrimComplete,
  onCancel,
}: VideoTrimmerProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(duration);
  const [isPlaying, setIsPlaying] = useState(false);

  // Validate and clamp time values
  useEffect(() => {
    if (endTime <= startTime) {
      setEndTime(Math.min(startTime + 1, duration));
    }
  }, [startTime, endTime, duration]);

  useEffect(() => {
    if (startTime > endTime) {
      setStartTime(Math.max(endTime - 1, 0));
    }
  }, [endTime, startTime]);

  const trimMutation = useMutation({
    mutationFn: () => mediaAPI.trim(mediaId, startTime, endTime),
    onSuccess: (response) => {
      toast.success(t('videotrim_success', 'Video trimmed successfully'));
      onTrimComplete(response.data);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('videotrim_error', 'Failed to trim video')));
    },
  });

  const handlePlayPreview = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = startTime;
      videoRef.current.play();
      setIsPlaying(true);

      // Stop playing at end time
      const handleTimeUpdate = () => {
        if (videoRef.current && videoRef.current.currentTime >= endTime) {
          videoRef.current.pause();
          setIsPlaying(false);
          videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        }
      };

      videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
  };

  const handleApplyTrim = () => {
    if (startTime >= endTime) {
      toast.error(t('videotrim_invalid_range', 'Invalid time range'));
      return;
    }
    trimMutation.mutate();
  };

  const trimmedDuration = endTime - startTime;
  const isLoading = trimMutation.isPending;

  return (
    <div className="flex flex-col gap-6 p-6 bg-white dark:bg-slate-900 rounded-lg max-w-2xl w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
          {t('videotrim_title', 'Trim Video')}
        </h2>
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 disabled:opacity-50"
          aria-label={t('common_cancel', 'Cancel')}
        >
          <X size={24} />
        </button>
      </div>

      {/* Video Preview */}
      <div className="bg-black rounded-lg overflow-hidden">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          src={videoUrl}
          controls={false}
          className="w-full h-auto max-h-96 object-contain"
          onEnded={handleVideoEnded}
        />
      </div>

      {/* Time Display */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {t('videotrim_start_time', 'Start Time')}
          </label>
          <div className="text-lg font-mono text-slate-900 dark:text-white">
            {formatTime(startTime)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {t('videotrim_end_time', 'End Time')}
          </label>
          <div className="text-lg font-mono text-slate-900 dark:text-white">
            {formatTime(endTime)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {t('videotrim_duration', 'Duration')}
          </label>
          <div className="text-lg font-mono text-slate-900 dark:text-white">
            {formatTime(trimmedDuration)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {t('videotrim_total_duration', 'Total')}
          </label>
          <div className="text-lg font-mono text-slate-900 dark:text-white">
            {formatTime(duration)}
          </div>
        </div>
      </div>

      {/* Start Time Slider */}
      <div className="flex flex-col gap-2">
        <label htmlFor="start-slider" className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('videotrim_start_time', 'Start Time')}
        </label>
        <input
          id="start-slider"
          type="range"
          min="0"
          max={duration}
          step="0.1"
          value={startTime}
          onChange={(e) => setStartTime(Math.min(parseFloat(e.target.value), endTime - 0.1))}
          disabled={isLoading}
          className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* End Time Slider */}
      <div className="flex flex-col gap-2">
        <label htmlFor="end-slider" className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('videotrim_end_time', 'End Time')}
        </label>
        <input
          id="end-slider"
          type="range"
          min="0"
          max={duration}
          step="0.1"
          value={endTime}
          onChange={(e) => setEndTime(Math.max(parseFloat(e.target.value), startTime + 0.1))}
          disabled={isLoading}
          className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Play Preview Button */}
      <button
        onClick={handlePlayPreview}
        disabled={isLoading || isPlaying}
        className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-900 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Play size={18} />
        {t('videotrim_preview', 'Play Preview')}
      </button>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('common_cancel', 'Cancel')}
        </button>
        <button
          onClick={handleApplyTrim}
          disabled={isLoading}
          aria-busy={isLoading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading && <Loader2 size={18} className="animate-spin" />}
          {isLoading ? t('common_loading', 'Loading...') : t('videotrim_apply', 'Apply Trim')}
        </button>
      </div>
    </div>
  );
}
